/**
 * AGENT-BC: "Revenue & Online Orders" combo chart — right card.
 *
 * Reads `reportData.rightChart` (PBI visual `12d423a1a2dccf50b506`, sourced
 * via Agent BA's `_evaluate_visual` orchestrator — that visual carries a
 * visual-level filter for online orders only, which is why this chart's
 * values are smaller than the left chart's).
 *
 * Same y-axis / color heuristic as the left chart (kept identical so both
 * cards read as a pair). All visual fidelity (rounded bars, line markers,
 * faint area gradient, rich tooltip) flows through `_chartUtils.ts`.
 *
 * If `payload.filterNote` is non-empty the chart renders it as an 11px
 * italic subtitle under the title (e.g. "Filtered to online orders").
 *
 * Anti-null x-axis defense lives in `useReportData.sanitizeXAxis`.
 */

import { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { dashboardChartTheme } from "../tokens";
import {
  AXIS_LABEL_FONT_SIZE,
  buildComboSeries,
  cssVar,
  decorateComboSeries,
  formatCount,
  formatCurrencyAbbrev,
  makeComboTooltipFormatter,
  makeTooltipBlock,
  STANDARD_GRID,
  STANDARD_LEGEND,
  type SeriesDecoration,
} from "./_chartUtils";
import {
  sanitizeXAxis,
  useReportData,
  type ComboChartPayload,
  type ComboSeries,
} from "./useReportData";
import { useUiStore } from "../state/uiStore";

const CHART_ID = "revenueOnlineOrders";
const FALLBACK_TITLE = "Revenue & Online Orders";

/**
 * Series colors pulled from the shared `--dash-series-N` design tokens so the
 * chart adapts to dark mode (the dark palette in tokens.css brightens these).
 * The light-mode token values equal the original spec hex (blue bar, orange
 * line, pink line), so the matched-pair look with the left chart is preserved;
 * the hard-coded hex are kept only as SSR/compute fallbacks. Read at call time
 * (inside the render path) so a theme flip re-resolves them.
 */
function specColors(): {
  bar: string;
  issued: string;
  converted: string;
} {
  return {
    bar: cssVar("--dash-series-1", "#1a73e8"), // Calculated Total — bars
    issued: cssVar("--dash-series-3", "#ff8c42"), // Issued / Online Orders
    converted: cssVar("--dash-series-5", "#e91e8c"), // Quotes Converted
  };
}

/**
 * Apply the token-derived colors on top of `decorateComboSeries`'s axis
 * assignment. Bars → blue. First line (Issued / Online Orders) → orange.
 * Second line (Converted) → pink. Any extra line series keeps the decorator's
 * pick.
 */
function applySpecColors(
  series: ComboSeries[],
  decorated: SeriesDecoration[],
): SeriesDecoration[] {
  const c = specColors();
  let lineSeen = 0;
  return decorated.map((d, i) => {
    const s = series[i];
    if (s.type === "bar") {
      return { ...d, color: c.bar };
    }
    lineSeen += 1;
    if (lineSeen === 1) return { ...d, color: c.issued };
    if (lineSeen === 2) return { ...d, color: c.converted };
    return d;
  });
}

export function RevenueOnlineOrdersChart(): JSX.Element {
  const { data, isLoading, isError } = useReportData();
  const theme = useUiStore((s) => s.theme);

  const payload: ComboChartPayload | undefined = data?.rightChart;
  const xAxisData = sanitizeXAxis(payload?.xAxis, CHART_ID);
  const series: ComboSeries[] = payload?.series ?? [];
  const title = (payload?.title && payload.title.trim()) || FALLBACK_TITLE;
  // Subtitle is driven by the backend's `filterNote` (server is the source of
  // truth for which filter the visual applies) instead of a hard-coded literal.
  // The "Revenue & Online Orders" visual is online-filtered server-side, so the
  // backend sends "Filtered to online orders"; other visuals send "" → no note.
  const filterNote = payload?.filterNote?.trim() || "";

  const hasData = xAxisData.length > 0 && series.length > 0;

  const option = useMemo(() => {
    if (!hasData) return null;

    const decorated = applySpecColors(series, decorateComboSeries(series));
    const mappedSeries = buildComboSeries(series, decorated);

    const currencyNames = new Set(
      series
        .filter((_, i) => decorated[i]?.yAxisIndex === 0)
        .map((s) => s.name),
    );

    const leftSeries = series.filter((_, i) => decorated[i]?.yAxisIndex === 0);
    const rightSeries = series.filter((_, i) => decorated[i]?.yAxisIndex === 1);
    const leftName = leftSeries[0]?.name || "Revenue";
    const rightName =
      rightSeries.length === 1 ? rightSeries[0].name : "Count";

    return dashboardChartTheme({
      legend: STANDARD_LEGEND,
      grid: STANDARD_GRID,
      tooltip: makeTooltipBlock(makeComboTooltipFormatter(currencyNames)),
      xAxis: {
        type: "category",
        data: xAxisData,
        boundaryGap: true,
        axisLabel: { fontSize: AXIS_LABEL_FONT_SIZE },
      },
      yAxis: [
        {
          type: "value",
          name: leftName,
          nameLocation: "middle" as const,
          nameGap: 48,
          nameTextStyle: {
            fontSize: AXIS_LABEL_FONT_SIZE,
            color: cssVar("--dash-chart-axis-label", "#5a6473"),
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: AXIS_LABEL_FONT_SIZE,
            formatter: (v: number) => formatCurrencyAbbrev(v),
          },
          splitLine: { show: true, lineStyle: { type: "dotted" as const } },
        },
        {
          type: "value",
          name: rightName,
          nameLocation: "middle" as const,
          nameGap: 40,
          nameTextStyle: {
            fontSize: AXIS_LABEL_FONT_SIZE,
            color: cssVar("--dash-chart-axis-label", "#5a6473"),
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: AXIS_LABEL_FONT_SIZE,
            formatter: (v: number) => formatCount(v),
          },
          splitLine: { show: false },
        },
      ],
      series: mappedSeries,
    });
  }, [hasData, series, xAxisData, theme]);

  return (
    <div className="dash-card">
      <h3 className="charts-row__title">{title}</h3>
      {filterNote.length > 0 ? (
        <div className="charts-row__subtitle">
          <em>{filterNote}</em>
        </div>
      ) : null}
      {isLoading ? (
        <div className="charts-row__empty charts-row__empty--shimmer" aria-busy="true">
          <span className="charts-row__shimmer" />
        </div>
      ) : isError ? (
        <div className="charts-row__empty charts-row__empty--message">Failed to load chart data</div>
      ) : !hasData || !option ? (
        <div className="charts-row__empty charts-row__empty--message">No chart data available</div>
      ) : (
        <ReactECharts
          className="charts-row__chart"
          option={option}
          notMerge
          lazyUpdate
          style={{ height: "100%", width: "100%" }}
        />
      )}
    </div>
  );
}

export default RevenueOnlineOrdersChart;
